import { useCallback, useEffect, useRef } from 'react';

type NotificationReadLifecycleOptions = {
  userId?: string | null;
  isNotificationsView: boolean;
  latestUnreadMillis: number | null;
};

type NotificationVisit = {
  entered: boolean;
};

type PendingViewedThrough = {
  userId: string | null;
  readThroughMillis: number | null;
};

const isFiniteMillis = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);

const mergeViewedThrough = (
  current: number | null,
  candidate: number,
) => current === null ? candidate : Math.max(current, candidate);

export const useNotificationReadLifecycle = ({
  userId,
  isNotificationsView,
  latestUnreadMillis,
}: NotificationReadLifecycleOptions) => {
  const currentUserId = userId ?? null;
  const pendingViewedThrough = useRef<PendingViewedThrough>({
    userId: currentUserId,
    readThroughMillis: null,
  });

  const pendingForCurrentUser = useCallback(() => {
    if (pendingViewedThrough.current.userId !== currentUserId) {
      pendingViewedThrough.current = {
        userId: currentUserId,
        readThroughMillis: null,
      };
    }

    return pendingViewedThrough.current;
  }, [currentUserId]);

  useEffect(() => {
    const pending = pendingForCurrentUser();

    if (
      currentUserId === null
      || !isNotificationsView
      || !isFiniteMillis(latestUnreadMillis)
    ) {
      return;
    }

    pending.readThroughMillis = mergeViewedThrough(
      pending.readThroughMillis,
      latestUnreadMillis,
    );
  }, [
    currentUserId,
    isNotificationsView,
    latestUnreadMillis,
    pendingForCurrentUser,
  ]);

  const beginNotificationVisit = useCallback((): NotificationVisit => {
    if (isNotificationsView) {
      return { entered: false };
    }

    const pending = pendingForCurrentUser();
    if (
      currentUserId !== null
      && isFiniteMillis(latestUnreadMillis)
    ) {
      pending.readThroughMillis = mergeViewedThrough(
        pending.readThroughMillis,
        latestUnreadMillis,
      );
    }

    return { entered: true };
  }, [
    currentUserId,
    isNotificationsView,
    latestUnreadMillis,
    pendingForCurrentUser,
  ]);

  const endNotificationVisit = useCallback(() => {
    if (!isNotificationsView) {
      return null;
    }

    const pending = pendingForCurrentUser();
    const readThroughMillis = pending.readThroughMillis;
    pending.readThroughMillis = null;

    return readThroughMillis;
  }, [isNotificationsView, pendingForCurrentUser]);

  const restoreViewedThrough = useCallback((millis: number) => {
    if (currentUserId === null || !isFiniteMillis(millis)) {
      return;
    }

    const pending = pendingForCurrentUser();
    pending.readThroughMillis = mergeViewedThrough(
      pending.readThroughMillis,
      millis,
    );
  }, [currentUserId, pendingForCurrentUser]);

  return {
    beginNotificationVisit,
    endNotificationVisit,
    restoreViewedThrough,
  };
};
